<!--
  -  Copyright (c) Qwoach LLC 2023. All rights reserved.
  -  Unauthorized copying of this file, via any medium is strictly prohibited
  -  Proprietary and confidential
  -  Created by Artem Bogush <artem@qwoach.com> on 10/10/2023
  -->

<template>
	<div class="editor-fixed-menu-root">
		<div
			class="menubar"
			:class="{'editor-fixed-menu-root__menubar--sticky': isAffixed}"
		>

				<button
					v-if="isToolbarOptionEnabled('bold')"
					class="menubar__button"
					:class="{ 'is-active': editor.isActive('bold')}"
					type="button"
					title="Bold"
					@click="commandWrapper('toggleBold', 'bold')"
				>
				<i class="fontello icon-bold"/>
			</button>

				<button
					v-if="isToolbarOptionEnabled('italic')"
					class="menubar__button"
					:class="{ 'is-active': editor.isActive('italic') }"
					type="button"
					title="Italic"
					@click="commandWrapper('toggleItalic', 'Italic')"
				>
				<i class="fontello icon-italic"/>
			</button>

				<button
					v-if="isToolbarOptionEnabled('strike')"
					class="menubar__button"
					:class="{ 'is-active': editor.isActive('strike') }"
					type="button"
					title="Strikethrough"
					@click="commandWrapper('toggleStrike', 'Strike')"
				>
				<i class="fontello icon-strike"/>
			</button>

				<button
					v-if="isToolbarOptionEnabled('underline')"
					class="menubar__button"
					:class="{ 'is-active': editor.isActive('underline') }"
					type="button"
					title="Underline"
					@click="commandWrapper('toggleUnderline', 'Underline')"
				>
				<i class="fontello icon-underline"/>
			</button>

				<button
					v-if="isToolbarOptionEnabled('heading1')"
					class="menubar__button"
					:class="{ 'is-active': editor.isActive('heading', { level: 1 }) }"
					type="button"
					title="Heading 1"
					@click="commandWrapper('toggleHeading', 'Heading 1', { level: 1 })"
				>
				H1
			</button>
				<button
					v-if="isToolbarOptionEnabled('heading2')"
					class="menubar__button"
					:class="{ 'is-active': editor.isActive('heading', { level: 2 }) }"
					type="button"
					title="Heading 2"
					@click="commandWrapper('toggleHeading', 'Heading 2', { level: 2 })"
				>
				H2
			</button>
				<button
					v-if="isToolbarOptionEnabled('heading3')"
					class="menubar__button"
					:class="{ 'is-active': editor.isActive('heading', { level: 3 }) }"
					type="button"
					title="Heading 3"
					@click="commandWrapper('toggleHeading', 'Heading 3', { level: 3 })"
				>
				H3
			</button>

				<button
					v-if="isToolbarOptionEnabled('bulletList')"
					class="menubar__button"
					:class="{ 'is-active': editor.isActive('bulletList') }"
					type="button"
					title="Bullet list"
					@click="commandWrapper('toggleBulletList', 'Bullet list')"
				>
				<i class="fontello icon-list-bullet"/>
			</button>

				<button
					v-if="isToolbarOptionEnabled('orderedList')"
					class="menubar__button"
					:class="{ 'is-active': editor.isActive('orderedList') }"
					type="button"
					title="Numbered list"
					@click="commandWrapper('toggleOrderedList', 'Ordered list')"
				>
				<i class="fontello icon-list-numbered"/>
			</button>

				<button
					v-if="isToolbarOptionEnabled('blockquote')"
					class="menubar__button"
					:class="{ 'is-active': editor.isActive('blockquote') }"
					type="button"
					title="Quote"
					@click="commandWrapper('toggleBlockquote', 'Blockquote')"
				>
				<i class="fontello icon-quote"/>
			</button>

				<button
					v-if="isToolbarOptionEnabled('link')"
					class="menubar__button"
					:class="{ 'is-active': editor.isActive('link') }"
					type="button"
					title="Link"
					@click="editLinkClicked"
				>
				<i class="fontello icon-link"/>
			</button>

			<EditorColorMenu
				v-if="isColorMenuEnabledComputed"
				:editor="editor"
				:toolbarOptions="toolbarOptions"/>

			<button
				v-if="isToolbarOptionEnabled('undo')"
				class="menubar__button"
				type="button"
				title="Undo"
				@click="commandWrapper('undo', 'Undo')"
			>
				<i class="fontello icon-ccw"/>
			</button>
			<button
				v-if="isToolbarOptionEnabled('redo')"
				class="menubar__button"
				type="button"
				title="Redo"
				@click="commandWrapper('redo', 'Redo')"
			>
				<i class="fontello icon-cw"/>
			</button>

		</div>
	</div>
</template>

<script>
import BaseComponent from "@/core/components/BaseComponent.vue";
import {Editor} from '@tiptap/vue-3'
import EditorColorMenu from "@/core/modules/Notes/view/EditorColorMenu.vue";

export default {
	name: "EditorFixedMenu",
	components: {EditorColorMenu},
	extends: BaseComponent,

	props: {
		editor: {Editor, required: true},
		isAffixed: {type: Boolean, default: false},
		toolbarOptions: {type: Object, default: () => ({})},
	},

	computed: {
		isTextColorEnabledComputed() {
			return this.toolbarOptions?.textColor === true
		},
		isTextHighlightEnabledComputed() {
			return this.toolbarOptions?.backgroundColor === true
		},
		isColorMenuEnabledComputed() {
			return this.isTextColorEnabledComputed || this.isTextHighlightEnabledComputed
		},
	},

	methods: {
		isToolbarOptionEnabled(optionKey) {
			return this.toolbarOptions?.[optionKey] !== false
		},

		commandWrapper(command, commandName, ...args){
			this.$analyticManager.trackEvent(commandName + " clicked", this.analyticsPropsMethod())
			return this.editor.chain().focus()[command](...args).run()
		},

		async editLinkClicked(){
			this.$analyticManager.trackEvent("Link clicked", this.analyticsPropsMethod())
			if(this.editor.isActive('link')){
				this.editor.commands.unsetLink()
			} else {
				this.$emit("linkClicked")
			}
		},
	},
}
</script>

<style lang="scss">
.editor-fixed-menu-root {
	&__menubar--sticky {
		background: white;
		box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
		//padding: 6px 0;
	}
}
</style>
